// ─────────────────────────────────────────────────────────────────────────────
// O documento que vai junto do ticket concluído.
//
// Portado do pdf_generator.js do Orion 1.4.1, com duas diferenças que são o
// motivo desta migração:
//
//   · lá as imagens eram empilhadas numa seção "Anexos" no FIM do documento.
//     Aqui cada imagem é desenhada NO PONTO DO TEXTO em que foi inserida — a
//     mesma ordem que o operador montou na tela;
//   · o layout deixou de ser "texto corrido numa página com faixa azul".
//
// ELE NÃO FALA DO TICKET. Nada de número, classificação, ocorrência,
// solicitante ou operador: quem recebe este PDF é o CLIENTE, e para ele esses
// campos são vocabulário interno do suporte. O documento é a situação relatada,
// e só isso — título, texto e as imagens no lugar em que foram postas.
//
// Os dados do atendimento continuam viajando no FormData de `montarEnvioDocumento`:
// o n8n precisa deles para saber a que ticket o arquivo pertence. Uma coisa é o
// que o documento MOSTRA; outra é o que o envio CARREGA.
//
// TAMANHO DA IMAGEM. Entra na largura útil da página, com teto de altura, e
// SEMPRE em escala proporcional (`Math.min(..., 1)`): um print pequeno não é
// esticado, um print de tela inteira é reduzido. É o que evita tanto a imagem
// borrada quanto a que ocupa três páginas sozinha.
//
// Roda no navegador e no Electron: pdf-lib é a mesma biblioteca nos dois, e o
// PDF é montado no cliente antes de subir. Não há caminho de arquivo aqui.
//
// O texto que chega aqui já passou pelo agente de formalização do documento
// (services/conclusaoDocumento.js). Este arquivo não sabe disso e não precisa
// saber: ele desenha o que recebe.
// ─────────────────────────────────────────────────────────────────────────────
import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';
// Com a extensão: o webpack resolve dos dois jeitos, mas o `node` do teste
// (scripts/teste-documento.mjs) só resolve o caminho completo. É o que permite
// conferir o PDF sem subir a aplicação.
import { blocosComAnexos } from './textoComAnexos.js';
import { LOGO_ORION_PNG_BASE64, LOGO_ORION_PROPORCAO } from '../assets/logoOrion.js';

// ── Layout ───────────────────────────────────────────────────────────────────
// As margens e o teto de altura da imagem são os mesmos do 1.4.1 — mexer neles
// muda o enquadramento de todos os documentos já emitidos, e o teste
// (scripts/teste-documento.mjs) confere esses números.
const PAGE_W = 595.28;          // A4 em pontos
const PAGE_H = 841.89;
const MARGIN_LEFT = 52;
const MARGIN_RIGHT = 52;
const HEADER_BOTTOM = 762;      // faixa do topo mais enxuta que a do 1.4.1
const HEADER_HEIGHT = PAGE_H - HEADER_BOTTOM;
const FOOTER_HEIGHT = 46;       // rodapé de uma linha, no lugar do bloco antigo
const CONTENT_BOTTOM = 74;
// Respiro entre a faixa do topo e a primeira linha. Antes eram 26pt e o texto
// encostava no azul — nas páginas seguintes, onde não há título, ficava pior
// ainda: a leitura começava colada na barra.
const CONTENT_TOP = 720;
const CONTENT_WIDTH = PAGE_W - MARGIN_LEFT - MARGIN_RIGHT;

const COR_NAVY = rgb(0.08, 0.20, 0.42);
const COR_AZUL_CLARO = rgb(0.72, 0.83, 0.95);
const COR_ACCENT = rgb(0.18, 0.46, 0.88);
const COR_TEXTO = rgb(0.13, 0.14, 0.16);
const COR_MUTED = rgb(0.42, 0.44, 0.48);
const COR_ROTULO = rgb(0.55, 0.57, 0.62);
const COR_LINHA = rgb(0.85, 0.86, 0.88);
const COR_MOLDURA = rgb(0.80, 0.82, 0.85);
const COR_FUNDO_FIGURA = rgb(0.972, 0.976, 0.984);
const COR_BRANCO = rgb(1, 1, 1);

const FS_TITULO = 17.5;
const LH_TITULO = 23;
const FS_BODY = 10.5;
const LH_BODY = 16.5;           // entrelinha folgada: o documento é lido, não consultado
const ESPACO_PARAGRAFO = 10;    // respiro ENTRE parágrafos, que o 1.4.1 não tinha
const FS_ROTULO = 7.5;
const FS_LEGENDA = 8.5;
const LH_LEGENDA = 12;
const FS_RODAPE = 7.5;

const IMG_MAX_W = CONTENT_WIDTH;
const IMG_MAX_H = 260;
const IMG_PADDING = 8;          // respiro entre a moldura e a figura

// ── Texto ────────────────────────────────────────────────────────────────────

/**
 * Ajusta o texto ao subset WinAnsi das fontes padrão do PDF.
 * Sem isto, um travessão ou uma aspa curly colada do Word derruba a geração
 * inteira com "WinAnsi cannot encode".
 */
export function sanitizar(texto) {
  if (texto == null) return '';
  return String(texto)
    .replace(/[‘’‚‛]/g, "'")
    .replace(/[“”„‟]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/…/g, '...')
    .replace(/[•◦▪▫]/g, '-')
    .replace(/ /g, ' ')
    // eslint-disable-next-line no-control-regex
    .replace(/[^\x09\x0A\x0D\x20-\xFF]/g, '');
}

/**
 * Tira do texto o que o editor deixou para trás.
 *
 * O campo de anotações aceita negrito/itálico e o texto do MySuite viaja com
 * `<br>`, `<b>`, `<div>`. Isso é legítimo lá — o MySuite renderiza. No PDF vira
 * lixo visível no meio da frase. O agente de formalização já limpa a maior
 * parte; esta é a rede de segurança para quando ele não roda.
 */
export function limparTags(texto) {
  if (texto == null) return '';
  return String(texto)
    .replace(/<\s*br\s*\/?\s*>/gi, '\n')
    .replace(/<\s*\/\s*(p|div|li|tr|h[1-6])\s*>/gi, '\n')
    .replace(/<\s*li[^>]*>/gi, '- ')
    .replace(/<[^>]{1,200}>/g, '')
    // Entidades que sobram quando a tag sai.
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/gi, "'")
    .replace(/[ \t]{2,}/g, ' ')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
}

/**
 * Quebra um texto na largura disponível.
 * Palavra maior que a linha (um caminho de rede, um hash) é quebrada caractere
 * a caractere — senão ela transbordaria a margem.
 */
export function quebrarTexto(texto, font, tamanho, larguraMax) {
  const linhas = [];

  String(texto).split(/\r?\n/).forEach((par) => {
    if (par.trim() === '') { linhas.push(''); return; }

    let atual = '';
    par.split(/\s+/).forEach((palavra) => {
      const tentativa = atual ? `${atual} ${palavra}` : palavra;
      if (font.widthOfTextAtSize(tentativa, tamanho) <= larguraMax) {
        atual = tentativa;
        return;
      }
      if (atual) { linhas.push(atual); atual = ''; }

      if (font.widthOfTextAtSize(palavra, tamanho) > larguraMax) {
        let buffer = '';
        for (const ch of palavra) {
          if (font.widthOfTextAtSize(buffer + ch, tamanho) <= larguraMax) {
            buffer += ch;
          } else {
            if (buffer) linhas.push(buffer);
            buffer = ch;
          }
        }
        if (buffer) atual = buffer;
      } else {
        atual = palavra;
      }
    });

    if (atual) linhas.push(atual);
  });

  return linhas;
}

// ── Imagem ───────────────────────────────────────────────────────────────────

/** Tira o prefixo `data:image/png;base64,` se existir. */
export function apenasBase64(str) {
  if (!str) return '';
  const s = String(str);
  const i = s.indexOf('base64,');
  return i >= 0 ? s.slice(i + 'base64,'.length).trim() : s.trim();
}

/** Formato pelo início do base64 — não dá para confiar no nome do arquivo. */
export function formatoDe(base64) {
  if (!base64) return null;
  if (base64.startsWith('iVBOR')) return 'png';
  if (base64.startsWith('/9j/')) return 'jpg';
  return null;
}

/**
 * base64 → Uint8Array.
 * No 1.x era `Buffer.from`, que só existe no Node. Aqui roda no navegador
 * também, então é `atob` — a mesma conversão, sem depender do Electron.
 */
export function bytesDeBase64(base64) {
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i += 1) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// ── Documento ────────────────────────────────────────────────────────────────

/**
 * Monta o PDF da situação relatada.
 *
 * Recebe só o que o documento MOSTRA. Os dados do ticket chegam a
 * `montarEnvioDocumento` e seguem no FormData, mas não entram no desenho.
 *
 * @param {object} dados
 * @param {string} dados.texto        texto formalizado, COM os marcadores das imagens
 * @param {object} dados.imagensPorId { id: { base64, dataUrl, legenda, nome } }
 * @param {string} [dados.titulo]     o título que o agente escreveu para a situação
 * @returns {Promise<Uint8Array>} os bytes do PDF
 */
export async function gerarDocumentoPdf({
  texto,
  imagensPorId = {},
  titulo = '',
}) {
  const pdf = await PDFDocument.create();
  const fonte = await pdf.embedFont(StandardFonts.Helvetica);
  const negrito = await pdf.embedFont(StandardFonts.HelveticaBold);
  const italico = await pdf.embedFont(StandardFonts.HelveticaOblique);

  let pagina = null;
  let cursorY = CONTENT_TOP;
  const paginas = [];             // guardadas para o rodapé saber o total

  // A logo entra uma vez e é reusada em todas as páginas: embutir por página
  // multiplicaria o PNG dentro do arquivo.
  let logo = null;
  try {
    logo = await pdf.embedPng(bytesDeBase64(LOGO_ORION_PNG_BASE64));
  } catch (e) {
    logo = null;   // sem a logo o documento sai igual, só sem a marca
  }

  // ── cabeçalho ──────────────────────────────────────────────────────────────
  // Faixa navy fina com a logo do Orion à esquerda e o nome da casa ao lado. A
  // régua accent embaixo é o que amarra o documento à identidade do app.
  //
  // O lado direito ficou VAZIO de propósito: era onde ia o número do ticket.
  function desenharCabecalho(p) {
    p.drawRectangle({
      x: 0, y: HEADER_BOTTOM + 3, width: PAGE_W, height: HEADER_HEIGHT - 3, color: COR_NAVY,
    });
    p.drawRectangle({ x: 0, y: HEADER_BOTTOM, width: PAGE_W, height: 3, color: COR_ACCENT });

    let x = MARGIN_LEFT;
    if (logo) {
      const alturaLogo = 26;
      p.drawImage(logo, {
        x, y: 785, width: alturaLogo * LOGO_ORION_PROPORCAO, height: alturaLogo,
      });
      x += alturaLogo * LOGO_ORION_PROPORCAO + 12;
    }

    p.drawText(sanitizar('SPACE SISTEMAS'), {
      x, y: 800, size: 12.5, font: negrito, color: COR_BRANCO,
    });
    p.drawText(sanitizar('Registro de atendimento'), {
      x, y: 787, size: 8.5, font: fonte, color: COR_AZUL_CLARO,
    });
  }

  // ── rodapé ─────────────────────────────────────────────────────────────────
  // Uma linha só, discreta. Os dados do atendimento saíram daqui e subiram para
  // a ficha de identificação da primeira página: repetir operador e data em toda
  // página é ruído num documento que o cliente vai ler.
  function desenharRodape(p, numPagina, total) {
    p.drawLine({
      start: { x: MARGIN_LEFT, y: FOOTER_HEIGHT }, end: { x: PAGE_W - MARGIN_RIGHT, y: FOOTER_HEIGHT },
      thickness: 0.6, color: COR_LINHA,
    });

    p.drawText(sanitizar('Documento gerado pelo Orion - Space Sistemas'), {
      x: MARGIN_LEFT, y: FOOTER_HEIGHT - 15, size: FS_RODAPE, font: fonte, color: COR_MUTED,
    });

    const paginacao = sanitizar(`${numPagina} de ${total}`);
    p.drawText(paginacao, {
      x: PAGE_W - MARGIN_RIGHT - fonte.widthOfTextAtSize(paginacao, FS_RODAPE),
      y: FOOTER_HEIGHT - 15, size: FS_RODAPE, font: fonte, color: COR_MUTED,
    });
  }

  function novaPagina() {
    pagina = pdf.addPage([PAGE_W, PAGE_H]);
    paginas.push(pagina);
    desenharCabecalho(pagina);
    cursorY = CONTENT_TOP;
  }

  function garantirEspaco(altura) {
    if (cursorY - altura < CONTENT_BOTTOM) novaPagina();
  }

  /** Uma linha de texto na posição do cursor, descendo o cursor. */
  function escreverLinhas(linhas, { font, size, lineHeight, color, x = MARGIN_LEFT }) {
    for (const linha of linhas) {
      garantirEspaco(lineHeight);
      if (linha !== '') {
        pagina.drawText(linha, { x, y: cursorY, size, font, color });
      }
      cursorY -= lineHeight;
    }
  }

  /**
   * Uma linha do corpo, com as palavras distribuídas até a margem direita.
   *
   * POR QUE JUSTIFICAR. O documento vai para o cliente e é lido como carta, não
   * consultado como relatório. Alinhado só à esquerda, a margem direita fica
   * serrilhada e o texto parece rascunho colado num papel timbrado.
   *
   * A justificação é feita palavra a palavra, com o espaço calculado — o PDF não
   * tem entrelinha elástica. Duas travas evitam o efeito contrário (os "rios"
   * brancos que atravessam o parágrafo): linha com uma palavra só e linha em que
   * o espaço passaria de 2,4× o normal saem sem justificar.
   */
  function escreverLinhaJustificada(linha, { font, size, color, x, larguraMax }) {
    const palavras = linha.split(' ').filter(Boolean);
    const espacoNormal = font.widthOfTextAtSize(' ', size);

    if (palavras.length > 1) {
      const larguraPalavras = palavras.reduce(
        (soma, p) => soma + font.widthOfTextAtSize(p, size), 0,
      );
      const espaco = (larguraMax - larguraPalavras) / (palavras.length - 1);

      if (espaco >= espacoNormal * 0.8 && espaco <= espacoNormal * 2.4) {
        let cx = x;
        for (const palavra of palavras) {
          pagina.drawText(palavra, { x: cx, y: cursorY, size, font, color });
          cx += font.widthOfTextAtSize(palavra, size) + espaco;
        }
        return;
      }
    }

    pagina.drawText(linha, { x, y: cursorY, size, font, color });
  }

  /**
   * Um parágrafo do corpo, justificado — menos a última linha, que termina onde
   * a frase termina (esticar a última linha é o erro clássico da justificação).
   *
   * VIÚVA. Um parágrafo que começa com uma linha só no pé da página deixa o
   * papel com cara de erro de impressão. Quando não cabem pelo menos duas
   * linhas, ele começa na página seguinte inteiro.
   */
  function escreverParagrafo(paragrafo, { font, size, lineHeight, color }) {
    const linhas = quebrarTexto(sanitizar(paragrafo), font, size, CONTENT_WIDTH);
    if (linhas.length > 1 && cursorY - lineHeight * 2 < CONTENT_BOTTOM) novaPagina();

    linhas.forEach((linha, i) => {
      garantirEspaco(lineHeight);
      if (linha !== '') {
        const ultima = i === linhas.length - 1;
        if (ultima) pagina.drawText(linha, { x: MARGIN_LEFT, y: cursorY, size, font, color });
        else {
          escreverLinhaJustificada(linha, {
            font, size, color, x: MARGIN_LEFT, larguraMax: CONTENT_WIDTH,
          });
        }
      }
      cursorY -= lineHeight;
    });
  }

  novaPagina();

  // ── 1) Abertura: rótulo, título e régua ────────────────────────────────────
  //
  // A hierarquia é a de uma página de abertura: um rótulo pequeno dizendo o que
  // o documento é, o título logo abaixo e uma régua que fecha o bloco. Antes o
  // rótulo vinha DEPOIS do título, repetindo "situação reportada" duas vezes na
  // mesma dobra da página.
  pagina.drawText(sanitizar('SITUAÇÃO REPORTADA'), {
    x: MARGIN_LEFT, y: cursorY, size: FS_ROTULO, font: negrito, color: COR_ROTULO,
  });
  cursorY -= 20;

  // O título quem escreve é o agente do documento, a partir do próprio relato
  // (ver services/conclusaoDocumento.js). Se ele não devolveu nada, o documento
  // abre com o rótulo genérico — e NÃO com o assunto do ticket, que é justamente
  // o tipo de dado que não deve aparecer aqui.
  escreverLinhas(
    quebrarTexto(sanitizar(titulo || 'Situação reportada'), negrito, FS_TITULO, CONTENT_WIDTH),
    { font: negrito, size: FS_TITULO, lineHeight: LH_TITULO, color: COR_NAVY },
  );
  cursorY -= 2;

  // Régua fina sob o título: separa o título do corpo sem precisar de caixa,
  // faixa ou ficha — o documento inteiro se apoia em texto e espaço.
  garantirEspaco(16);
  pagina.drawRectangle({ x: MARGIN_LEFT, y: cursorY + 4, width: 52, height: 2.5, color: COR_ACCENT });
  cursorY -= 26;

  // ── 3) Corpo: texto e imagens NA ORDEM ESCRITA ─────────────────────────────
  //
  // É aqui que este gerador difere do 1.4.1. Os blocos vêm do parser, que lê o
  // texto e devolve parágrafos e imagens intercalados na sequência em que
  // aparecem — a mesma que o operador montou na tela.
  const blocos = blocosComAnexos(limparTags(texto), imagensPorId);

  let numeroFigura = 0;

  for (const bloco of blocos) {
    if (bloco.tipo === 'texto') {
      // Parágrafo a parágrafo, para o respiro entre eles ser controlado aqui e
      // não depender de linha em branco no texto de origem.
      //
      // for..of, e não forEach: o callback fecharia sobre `cursorY`, que muda a
      // cada linha escrita e a cada quebra de página.
      let primeiro = true;
      for (const paragrafo of bloco.paragrafos) {
        if (!primeiro) cursorY -= ESPACO_PARAGRAFO;
        primeiro = false;
        escreverParagrafo(paragrafo, {
          font: fonte, size: FS_BODY, lineHeight: LH_BODY, color: COR_TEXTO,
        });
      }
      continue;
    }

    // ── imagem ──
    const base64 = apenasBase64(bloco.base64 || bloco.dataUrl);
    const formato = formatoDe(base64);
    if (!base64 || !formato) continue;   // formato que o PDF não desenha: pula

    let figura = null;
    try {
      const bytes = bytesDeBase64(base64);
      // eslint-disable-next-line no-await-in-loop
      figura = formato === 'png' ? await pdf.embedPng(bytes) : await pdf.embedJpg(bytes);
    } catch (e) {
      figura = null;                     // imagem corrompida não derruba o documento
    }
    if (!figura) continue;

    numeroFigura += 1;

    // TAMANHO: proporção preservada, com teto de largura e de altura. O `1`
    // no mínimo é o que impede uma imagem pequena de ser ampliada e sair
    // borrada no papel. O padding da moldura entra no teto de largura para a
    // caixa inteira caber na margem.
    const escala = Math.min(
      (IMG_MAX_W - IMG_PADDING * 2) / figura.width,
      IMG_MAX_H / figura.height,
      1,
    );
    const largura = figura.width * escala;
    const altura = figura.height * escala;

    // Legenda numerada. "Figura N" sozinha quando não há texto — o número é o
    // que permite o corpo do documento citar a imagem ("conforme a Figura 2").
    //
    // O número sai em negrito e a descrição em itálico, em linhas separadas
    // quando a descrição é longa: tudo na mesma linha itálica fazia a legenda
    // desaparecer sob a figura.
    const rotuloFigura = `Figura ${numeroFigura}`;
    const legendaTexto = sanitizar(bloco.legenda || '').trim();
    const linhasLegenda = legendaTexto
      ? quebrarTexto(legendaTexto, italico, FS_LEGENDA, CONTENT_WIDTH - 80)
      : [];

    // A imagem não pode ser partida entre duas páginas: ou cabe inteira aqui,
    // ou começa na próxima — junto da legenda, que sem ela não diz nada.
    const alturaCaixa = altura + IMG_PADDING * 2;
    const alturaLegenda = (linhasLegenda.length + 1) * LH_LEGENDA;
    const necessario = alturaCaixa + 10 + alturaLegenda + 20;
    if (cursorY - necessario < CONTENT_BOTTOM) novaPagina();

    // Respiro ANTES da figura, maior do que o de entre parágrafos: a imagem é
    // um bloco à parte, e não a continuação da frase anterior.
    cursorY -= 10;

    const caixaX = MARGIN_LEFT + (CONTENT_WIDTH - largura - IMG_PADDING * 2) / 2;
    const caixaY = cursorY - alturaCaixa;

    // Moldura: fundo cinza levíssimo e borda fina. Print de tela costuma ter
    // fundo branco e, sem isso, ele "vaza" no papel — não se vê onde a imagem
    // começa nem onde termina.
    pagina.drawRectangle({
      x: caixaX, y: caixaY, width: largura + IMG_PADDING * 2, height: alturaCaixa,
      color: COR_FUNDO_FIGURA, borderColor: COR_MOLDURA, borderWidth: 0.7,
    });

    pagina.drawImage(figura, {
      x: caixaX + IMG_PADDING,
      y: caixaY + IMG_PADDING,
      width: largura,
      height: altura,
    });

    cursorY -= alturaCaixa + 12;

    // Rótulo da figura, centralizado sob a moldura.
    garantirEspaco(LH_LEGENDA);
    pagina.drawText(rotuloFigura, {
      x: MARGIN_LEFT + (CONTENT_WIDTH - negrito.widthOfTextAtSize(rotuloFigura, FS_LEGENDA)) / 2,
      y: cursorY, size: FS_LEGENDA, font: negrito, color: COR_ROTULO,
    });
    cursorY -= LH_LEGENDA;

    // for..of, e não forEach: o callback dentro do laço fecharia sobre
    // `pagina` e `cursorY`, que mudam a cada quebra de página.
    for (const linha of linhasLegenda) {
      garantirEspaco(LH_LEGENDA);
      pagina.drawText(linha, {
        x: MARGIN_LEFT + (CONTENT_WIDTH - italico.widthOfTextAtSize(linha, FS_LEGENDA)) / 2,
        y: cursorY, size: FS_LEGENDA, font: italico, color: COR_MUTED,
      });
      cursorY -= LH_LEGENDA;
    }

    cursorY -= 16;
  }

  // ── 4) Rodapé, agora que o total de páginas é conhecido ────────────────────
  paginas.forEach((p, i) => desenharRodape(p, i + 1, paginas.length));

  return pdf.save({ useObjectStreams: false });
}

/**
 * Monta o PDF e devolve o FormData que o webhook `gerar-documento` espera.
 *
 * O 1.4.1 manda o binário, e não JSON: o documento é montado no app e o n8n só
 * o arquiva no Drive. Manter o mesmo contrato é o que evita ter de mexer no
 * fluxo do n8n — e é por isso que os campos de texto vão com os mesmos nomes.
 */
export async function montarEnvioDocumento(dados) {
  const bytes = await gerarDocumentoPdf(dados);
  const arquivo = new Blob([bytes], { type: 'application/pdf' });

  const form = new FormData();
  form.append('arquivo', arquivo, `ticket_${dados.numeroTicket || 'sem-numero'}.pdf`);
  form.append('numero_ticket', dados.numeroTicket || '');
  form.append('solicitante', dados.solicitante || '');
  form.append('tipo_atendimento', dados.tipoAtendimento || '');
  form.append('assunto', dados.assunto || '');
  form.append('classificacao', dados.classificacao || '');
  form.append('ocorrencia', dados.ocorrencia || '');
  form.append('criticidade', dados.criticidade || '');
  form.append('operador_nome', dados.operadorNome || '');
  form.append('operador_email', dados.operadorEmail || '');
  form.append('data_hora', dados.dataHora || '');
  form.append('tag', 'concluido');

  return form;
}
